/**
 * WebSocket Relay Client
 *
 * Connects to relay WebSocket, then forwards messages to upstream Kalshi WS.
 * Auto-reconnects with exponential backoff on connection loss.
 */

import type { WsRelayMessage, WsRelayFrame } from '@galactus/shared';
import { ReconnectState } from './reconnect';

// In production, always derive WS URL from the current page's host so the
// connection works regardless of which domain serves the dashboard (e.g.
// dev.g1.newbx.com vs streamrift.dev.g1.newbx.com).  The env var override
// is only respected in dev mode for custom local relay setups.
const envRelayWsUrl = (import.meta.env.VITE_RELAY_WS_URL as string | undefined)?.trim();
const RELAY_WS_URL =
  envRelayWsUrl && envRelayWsUrl.length > 0
    ? envRelayWsUrl
    : import.meta.env.DEV
      ? 'ws://localhost:8787/relay/ws'
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/relay/ws`;

const MAX_RECONNECT_ATTEMPTS = 5;

export interface RelayWsClient {
  connect(streamId: string, upstreamUrl: string, headers: Record<string, string>): Promise<void>;
  send(streamId: string, payload: unknown): void;
  subscribe(streamId: string, payload?: unknown): void;
  close(streamId: string): void;
  onMessage(callback: (frame: WsRelayFrame) => void): void;
  /** Listen for non-relay-frame messages (e.g., telegram:cancel) */
  onRawMessage(callback: (data: Record<string, unknown>) => void): void;
  onError(callback: (error: Error) => void): void;
  /** Fires when the relay WebSocket connection is lost */
  onClose(callback: () => void): void;
  /** Fires when the relay WebSocket reconnects successfully */
  onReconnect(callback: () => void): void;
  disconnect(): void;
}

/**
 * Create WebSocket relay client with auto-reconnect
 */
export function createRelayWs(): RelayWsClient {
  let ws: WebSocket | null = null;
  let stopped = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const reconnectState = new ReconnectState();

  const messageCallbacks: Set<(frame: WsRelayFrame) => void> = new Set();
  const rawMessageCallbacks: Set<(data: Record<string, unknown>) => void> = new Set();
  const errorCallbacks: Set<(error: Error) => void> = new Set();
  const closeCallbacks: Set<() => void> = new Set();
  const reconnectCallbacks: Set<() => void> = new Set();

  // Track active stream configs for replay on reconnect
  const streamConfigs: Map<string, { upstreamUrl: string; headers: Record<string, string> }> =
    new Map();

  const doConnect = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (stopped) {
        reject(new Error('Client stopped'));
        return;
      }
      if (ws && ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      ws = new WebSocket(RELAY_WS_URL);

      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000);

      ws.onopen = () => {
        clearTimeout(timeout);
        const wasReconnecting = reconnectState.state === 'reconnecting';
        reconnectState.onConnected();
        resolve();

        if (wasReconnecting) {
          // Re-establish upstream streams
          for (const [streamId, config] of streamConfigs.entries()) {
            try {
              sendMessage({
                op: 'connect',
                id: streamId,
                url: config.upstreamUrl,
                headers: config.headers,
              });
            } catch {
              // Will be picked up by error handlers
            }
          }
          reconnectCallbacks.forEach((cb) => cb());
        }
      };

      ws.onerror = (error) => {
        clearTimeout(timeout);
        // Only broadcast errors on first attempt — suppress during reconnect retries to avoid log flood
        if (reconnectState.attempt === 0) {
          errorCallbacks.forEach((cb) => cb(new Error('WebSocket connection failed')));
        }
        reject(error);
      };

      ws.onmessage = (event) => {
        reconnectState.onMessage();
        try {
          const data = JSON.parse(event.data);
          // Relay frames have an `id` field; other messages (e.g., telegram:cancel) don't
          if (data && typeof data === 'object' && 'id' in data) {
            messageCallbacks.forEach((cb) => cb(data as WsRelayFrame));
          } else {
            rawMessageCallbacks.forEach((cb) => cb(data as Record<string, unknown>));
          }
        } catch (error) {
          console.error('Failed to parse relay message:', error);
        }
      };

      ws.onclose = () => {
        ws = null;
        if (stopped) return;

        // Only fire close callbacks on first disconnect, not on every retry
        if (reconnectState.state !== 'reconnecting') {
          closeCallbacks.forEach((cb) => cb());
        }
        scheduleReconnect();
      };
    });
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    if (reconnectState.attempt >= MAX_RECONNECT_ATTEMPTS) {
      reconnectState.giveUp();
      errorCallbacks.forEach((cb) =>
        cb(new Error(`Relay reconnect failed after ${MAX_RECONNECT_ATTEMPTS} attempts`))
      );
      return;
    }

    const { nextDelayMs } = reconnectState.onDisconnected();
    reconnectTimer = setTimeout(() => {
      doConnect().catch(() => {
        // doConnect rejection triggers ws.onerror → scheduleReconnect via onclose
      });
    }, nextDelayMs);
  };

  const sendMessage = (message: WsRelayMessage) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }
    ws.send(JSON.stringify(message));
  };

  return {
    async connect(streamId: string, upstreamUrl: string, headers: Record<string, string>) {
      // Store config for reconnect replay
      streamConfigs.set(streamId, { upstreamUrl, headers });
      await doConnect();
      sendMessage({
        op: 'connect',
        id: streamId,
        url: upstreamUrl,
        headers,
      });
    },

    send(streamId: string, payload: unknown) {
      sendMessage({
        op: 'send',
        id: streamId,
        payload,
      });
    },

    subscribe(streamId: string, payload?: unknown) {
      sendMessage({
        op: 'subscribe',
        id: streamId,
        payload,
      });
    },

    close(streamId: string) {
      streamConfigs.delete(streamId);
      // Guard: no-op if WebSocket not connected (avoids crash in cleanup functions)
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      sendMessage({
        op: 'close',
        id: streamId,
      });
    },

    onMessage(callback: (frame: WsRelayFrame) => void) {
      messageCallbacks.add(callback);
    },

    onRawMessage(callback: (data: Record<string, unknown>) => void) {
      rawMessageCallbacks.add(callback);
    },

    onError(callback: (error: Error) => void) {
      errorCallbacks.add(callback);
    },

    onClose(callback: () => void) {
      closeCallbacks.add(callback);
    },

    onReconnect(callback: () => void) {
      reconnectCallbacks.add(callback);
    },

    disconnect() {
      stopped = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.close();
        ws = null;
      }
      reconnectState.reset();
      streamConfigs.clear();
      messageCallbacks.clear();
      rawMessageCallbacks.clear();
      errorCallbacks.clear();
      closeCallbacks.clear();
      reconnectCallbacks.clear();
    },
  };
}
